import { useEffect, useState } from "react";
import { differenceInDays } from "date-fns";
import {
  Box,
  Paper,
  Typography,
  IconButton,
  CircularProgress,
  Backdrop,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import {
  GRADIENT_BLACK_COLOR,
  GRADIENT_BLACK_COLOR_TEXT,
  INPUT_COLOR_BORDER,
} from "../../utils/colors";
import { startSaveUserNPSRating } from "../../actions/adsCampaigns";
import { useDispatchApp, useSelectorApp } from "../../lib/redux";
import {
  NPS_FORM_TITLE,
  NPS_LABEL_EXTREMELY_LIKELY,
  NPS_LABEL_NOT_LIKELY_AT_ALL,
  NPS_SAVING_RATING,
  NPS_THANK_YOU_DESCRIPTION,
  NPS_THANK_YOU_TITLE,
} from "../../i18n/keysTranslations";
import { useTranslationApp } from "../../lib/i18next";
import types from "../../types";
import { getNameBusinessTheme } from "../../actions/getters";
import {
  getPathDatabaseByRole,
  startGetCreationTime,
} from "../../actions/auth";

const DISABLE_NPS_FORM = false;
const MIN_ACCOUNT_AGE_DAYS_FOR_NPS = 60;

const NPS_SCORE_BOX_SX = {
  fontWeight: "bold",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "10px",
  cursor: "pointer",
  userSelect: "none",
  transition: "all 0.2s ease-in-out",
  background: GRADIENT_BLACK_COLOR,
  color: GRADIENT_BLACK_COLOR_TEXT,
  flex: { xs: "1 1 0", md: "0 0 auto" },
  minWidth: { xs: 0, md: "35px" },
  width: { xs: "auto", md: "35px" },
  maxWidth: { xs: "none", md: "35px" },
  height: { xs: 46, md: 50 },
  fontSize: { xs: "0.7rem", sm: "0.8125rem", md: "1rem" },
  "&:hover": {
    transform: "scale(1.05)",
  },
};

const NpsForm = () => {
  const { t } = useTranslationApp();
  const [isSuccess, setIsSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isAccountAgeValid, setIsAccountAgeValid] = useState(false);
  const dispatch = useDispatchApp();

  const { userID } = dispatch(getPathDatabaseByRole());
  const NAME_BUSINESS = dispatch(getNameBusinessTheme());
  const npsState = useSelectorApp(
    (state) => state.ui.npsForm || { shouldShow: false, actionType: null }
  );

  const getIsAccountAgeValid = async () => {
    const creationTime = await dispatch(startGetCreationTime());

    if (!creationTime) return setIsAccountAgeValid(false);
    setIsAccountAgeValid(
      differenceInDays(new Date(), new Date(creationTime)) >=
        MIN_ACCOUNT_AGE_DAYS_FOR_NPS
    );
    return true;
  };

  useEffect(() => {
    getIsAccountAgeValid();
    // eslint-disable-next-line
  }, [userID]);

  const onScoreSelect = async (score) => {
    if (!npsState.actionType) return;

    setIsLoading(true);

    try {
      const startTime = Date.now();

      const result = await dispatch(
        startSaveUserNPSRating({
          rating: score,
          actionType: npsState.actionType,
        })
      );

      const elapsedTime = Date.now() - startTime;
      const remainingTime = Math.max(0, 2000 - elapsedTime);

      if (remainingTime > 0) {
        await new Promise((resolve) => setTimeout(resolve, remainingTime));
      }

      if (result.ok) {
        setIsLoading(false);
        setIsSuccess(true);
        setTimeout(() => {
          onClose();
        }, 7000);
      } else {
        console.error("Error saving NPS rating:", result.message);
        setIsLoading(false);
      }
    } catch (error) {
      console.error("Error al guardar la calificación NPS:", error);
      setIsLoading(false);
    }
  };

  const onClose = () => {
    dispatch({ type: types.HIDE_NPS_FORM });
    setIsSuccess(false);
  };

  if (!isAccountAgeValid) return null;
  if (!npsState.shouldShow || DISABLE_NPS_FORM) return null;

  return (
    <Box
      sx={{
        position: "fixed",
        top: 68,
        left: { xs: 15, md: "auto" },
        right: 15,
        display: "flex",
        justifyContent: "center",
        zIndex: 9999,
        boxSizing: "border-box",
      }}
    >
      <Paper
        elevation={1}
        sx={{
          width: { xs: "100%", md: "auto" },
          maxWidth: 450,
          boxSizing: "border-box",
          mx: "auto",
          p: 3,
          borderRadius: 2,
          border: `1px solid ${INPUT_COLOR_BORDER}`,
          backgroundColor: "background.paper",
          position: "relative",
        }}
      >
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            color: "text.secondary",
            "&:hover": {
              backgroundColor: "rgba(0, 0, 0, 0.04)",
            },
          }}
          size="small"
        >
          <CloseIcon fontSize="small" />
        </IconButton>

        {isSuccess ? (
          <Box sx={{ textAlign: "center", mb: 3 }}>
            <CheckCircleIcon
              sx={{
                fontSize: 48,
                color: "success.main",
                mb: 2,
              }}
            />
            <Typography
              variant="h6"
              component="h3"
              gutterBottom
              sx={{ fontWeight: 500, color: "text.primary" }}
            >
              {t(NPS_THANK_YOU_TITLE)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t(NPS_THANK_YOU_DESCRIPTION, { NAME_BUSINESS })}
            </Typography>
          </Box>
        ) : (
          <Typography
            variant="h6"
            component="h3"
            textAlign="center"
            gutterBottom
            sx={{ mb: 3, fontWeight: 500, color: "text.primary" }}
          >
            {t(NPS_FORM_TITLE, { NAME_BUSINESS })}
          </Typography>
        )}

        {!isSuccess && (
          <Box sx={{ position: "relative" }}>
            <Box
              sx={{
                display: "flex",
                flexWrap: "nowrap",
                justifyContent: "center",
                width: { xs: "100%", md: "auto" },
                mb: 1,
                gap: 0.2,
              }}
            >
              {[...Array(11)].map((_, index) => (
                <Box
                  key={index}
                  onClick={() => !isLoading && onScoreSelect(index)}
                  sx={{
                    ...NPS_SCORE_BOX_SX,
                    opacity: isLoading ? 0.6 : 1,
                    cursor: isLoading ? "not-allowed" : "pointer",
                  }}
                >
                  {index}
                </Box>
              ))}
            </Box>

            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: "0.75rem" }}
              >
                {t(NPS_LABEL_NOT_LIKELY_AT_ALL)}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: "0.75rem" }}
              >
                {t(NPS_LABEL_EXTREMELY_LIKELY)}
              </Typography>
            </Box>
          </Box>
        )}

        <Backdrop
          open={isLoading}
          sx={{
            position: "absolute",
            zIndex: 1,
            backgroundColor: "background.paper",
            borderRadius: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 2,
            }}
          >
            <CircularProgress size={40} />
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontWeight: 500 }}
            >
              {t(NPS_SAVING_RATING)}
            </Typography>
          </Box>
        </Backdrop>
      </Paper>
    </Box>
  );
};

export default NpsForm;
